import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { artifactAPI, profileAPI, cubeAPI, scheduleAPI, customerAPI, campaignAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, User, BarChart3, Users, CreditCard, Settings, LogOut, Menu,
  Box, Circle, Watch, Tag, QrCode, FileImage, FileText,
  Printer, Calendar, Clock, CheckCircle, UserPlus, Gift, Package, ChevronDown,
  ChevronUp, Eye, Link2, ShoppingBag, ClipboardList, CheckSquare, Square
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ARTIFACT_TYPES = [
  { value: 'card', label: 'NFC Card', icon: <CreditCard className="w-5 h-5" /> },
  { value: 'cube', label: 'QR Cube', icon: <Box className="w-5 h-5" /> },
  { value: 'ring', label: 'Smart Ring', icon: <Circle className="w-5 h-5" /> },
  { value: 'bracelet', label: 'Bracelet', icon: <Watch className="w-5 h-5" /> },
  { value: 'pendant', label: 'Pendant', icon: <Tag className="w-5 h-5" /> },
  { value: 'keychain', label: 'Keychain', icon: <QrCode className="w-5 h-5" /> },
];

const PROFILE_COLORS = {
  green: { bg: 'bg-emerald-500', label: 'Green' },
  blue: { bg: 'bg-blue-500', label: 'Blue' },
  red: { bg: 'bg-red-500', label: 'Red' },
  yellow: { bg: 'bg-yellow-500', label: 'Yellow' },
  purple: { bg: 'bg-purple-500', label: 'Purple' },
  black: { bg: 'bg-slate-900', label: 'Black' },
};

const ArtifactsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cubeConfig, setCubeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);

  // Campaign analytics
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState({});

  // Tab
  const [activeTab, setActiveTab] = useState('all');

  // Bulk campaign assignment
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCampaignAssign, setShowCampaignAssign] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isNewCampaign, setIsNewCampaign] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artifactsRes, profilesRes, cubeRes, schedulesRes, activeRes, custRes] = await Promise.all([
          artifactAPI.list(),
          profileAPI.list(),
          cubeAPI.get(),
          scheduleAPI.list(),
          scheduleAPI.getActiveProfile(),
          customerAPI.list(),
        ]);
        setArtifacts(artifactsRes.data);
        setProfiles(profilesRes.data);
        setCubeConfig(cubeRes.data);
        setSchedules(schedulesRes.data);
        setActiveProfile(activeRes.data);
        setCustomers(custRes.data);
      } catch (error) {
        toast.error('Failed to load artifacts');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAssignProfile = async (artifactId, profileId) => {
    try {
      await artifactAPI.update(artifactId, { assigned_profile_id: profileId || null });
      setArtifacts(artifacts.map(a =>
        a.artifact_id === artifactId ? { ...a, assigned_profile_id: profileId || null } : a
      ));
      toast.success(profileId ? 'Profile assigned!' : 'Profile unassigned');
    } catch (error) {
      toast.error('Failed to update artifact');
    }
  };

  const handleAssignCustomer = async (artifactId, customerId) => {
    try {
      await artifactAPI.update(artifactId, { customer_id: customerId || null });
      setArtifacts(artifacts.map(a =>
        a.artifact_id === artifactId ? { ...a, customer_id: customerId || null } : a
      ));
      toast.success(customerId ? 'Customer assigned!' : 'Customer unassigned');
    } catch (error) {
      toast.error('Failed to assign customer');
    }
  };

  const handleUpdateCubeConfig = async (color, profileId) => {
    try {
      const updateData = { [`${color}_profile_id`]: profileId || null };
      const response = await cubeAPI.update(updateData);
      setCubeConfig(response.data);
      toast.success('Cube configuration updated!');
    } catch (error) {
      toast.error('Failed to update cube');
    }
  };

  const loadCampaignAnalytics = async (campaignName) => {
    if (expandedCampaign === campaignName) {
      setExpandedCampaign(null);
      return;
    }
    try {
      const res = await campaignAPI.getAnalytics(campaignName);
      setCampaignStats(prev => ({ ...prev, [campaignName]: res.data }));
      setExpandedCampaign(campaignName);
    } catch (error) {
      toast.error('Failed to load campaign analytics');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const unassignedPersonal = artifacts.filter(a => a.mode !== 'giveaway');
    if (selectedIds.length === unassignedPersonal.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(unassignedPersonal.map(a => a.artifact_id));
    }
  };

  const existingCampaigns = [...new Set(artifacts.filter(a => a.campaign_name).map(a => a.campaign_name))];

  const handleBulkAssignCampaign = async () => {
    const name = isNewCampaign ? newCampaignName : campaignName;
    if (!name?.trim()) {
      toast.error('Please enter or select a campaign name');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one artifact');
      return;
    }
    try {
      const res = await artifactAPI.bulkAssignCampaign(selectedIds, name.trim());
      toast.success(res.data.message);
      setArtifacts(artifacts.map(a =>
        selectedIds.includes(a.artifact_id) ? { ...a, campaign_name: name.trim(), mode: 'giveaway' } : a
      ));
      setSelectedIds([]);
      setShowCampaignAssign(false);
      setCampaignName('');
      setNewCampaignName('');
      setIsNewCampaign(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign campaign');
    }
  };

  // Group giveaway artifacts by campaign
  const campaigns = {};
  artifacts.filter(a => a.mode === 'giveaway').forEach(a => {
    const name = a.campaign_name || 'Uncategorized';
    if (!campaigns[name]) campaigns[name] = [];
    campaigns[name].push(a);
  });

  const personalArtifacts = artifacts.filter(a => a.mode !== 'giveaway');
  const giveawayArtifacts = artifacts.filter(a => a.mode === 'giveaway');

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts', active: true },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center"><span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span></div>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p><p className="text-xs text-slate-500 truncate">{user?.email}</p></div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-5 h-5 mr-3" />Sign out</Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Artifacts</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              {selectedIds.length > 0 && (
                <Button onClick={() => setShowCampaignAssign(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="assign-campaign-btn">
                  <Gift className="w-4 h-4 mr-2" />
                  Assign to Campaign ({selectedIds.length})
                </Button>
              )}
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="artifacts-page">
          {/* Bulk Campaign Assignment Modal */}
          {showCampaignAssign && (
            <div className="bg-white rounded-xl border-2 border-amber-200 p-6 mb-6" data-testid="campaign-assign-form">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Assign {selectedIds.length} Artifact{selectedIds.length !== 1 ? 's' : ''} to Campaign
              </h3>
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => { setIsNewCampaign(false); }}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all ${!isNewCampaign ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="existing-campaign-option"
                >
                  <p className="text-sm font-medium text-slate-900">Existing Campaign</p>
                </button>
                <button
                  onClick={() => { setIsNewCampaign(true); }}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all ${isNewCampaign ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="new-campaign-option"
                >
                  <p className="text-sm font-medium text-slate-900">New Campaign</p>
                </button>
              </div>
              {!isNewCampaign ? (
                <div className="mb-4">
                  <Label>Select Campaign</Label>
                  {existingCampaigns.length > 0 ? (
                    <Select value={campaignName} onValueChange={setCampaignName}>
                      <SelectTrigger className="mt-1" data-testid="campaign-select"><SelectValue placeholder="Choose a campaign..." /></SelectTrigger>
                      <SelectContent>
                        {existingCampaigns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-slate-400 mt-1">No existing campaigns. Create a new one.</p>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <Label>Campaign Name</Label>
                  <Input className="mt-1" placeholder="e.g. Trade Show 2026" value={newCampaignName} onChange={e => setNewCampaignName(e.target.value)} data-testid="new-campaign-input" />
                </div>
              )}
              <div className="flex gap-3">
                <Button onClick={handleBulkAssignCampaign} className="bg-amber-500 hover:bg-amber-600" data-testid="confirm-campaign-btn">
                  Assign to Campaign
                </Button>
                <Button variant="outline" onClick={() => { setShowCampaignAssign(false); setSelectedIds([]); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1 mb-6" data-testid="artifact-tabs">
            {[
              { id: 'all', label: `All (${artifacts.length})` },
              { id: 'personal', label: `Personal (${personalArtifacts.length})` },
              { id: 'giveaway', label: `Campaigns (${giveawayArtifacts.length})` },
              { id: 'cube', label: 'QR Cube' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Cube Configuration Tab */}
          {activeTab === 'cube' && (
            <CubeConfigSection cubeConfig={cubeConfig} profiles={profiles} schedules={schedules} activeProfile={activeProfile} onUpdateCube={handleUpdateCubeConfig} userId={user?.user_id} />
          )}

          {/* Personal Artifacts with select */}
          {(activeTab === 'all' || activeTab === 'personal') && personalArtifacts.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Personal Artifacts ({personalArtifacts.length})
                </h3>
                <button onClick={toggleSelectAll} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium" data-testid="select-all-btn">
                  {selectedIds.length === personalArtifacts.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {personalArtifacts.map((artifact) => (
                  <ArtifactRow
                    key={artifact.artifact_id}
                    artifact={artifact}
                    profiles={profiles}
                    onAssignProfile={handleAssignProfile}
                    selected={selectedIds.includes(artifact.artifact_id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Giveaway Campaigns */}
          {(activeTab === 'all' || activeTab === 'giveaway') && Object.keys(campaigns).length > 0 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Campaigns</h3>
              {Object.entries(campaigns).map(([cName, campaignArtifacts]) => (
                <div key={cName} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => loadCampaignAnalytics(cName)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`campaign-${cName}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><Gift className="w-5 h-5 text-amber-600" /></div>
                      <div className="text-left">
                        <h4 className="font-semibold text-slate-900">{cName}</h4>
                        <p className="text-sm text-slate-500">{campaignArtifacts.length} artifact{campaignArtifacts.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    {expandedCampaign === cName ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>

                  {expandedCampaign === cName && (
                    <div className="border-t border-slate-200">
                      {campaignStats[cName] && (
                        <div className="px-6 py-4 bg-amber-50/50 border-b border-slate-200">
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                            {[
                              { label: 'Artifacts', value: campaignStats[cName].total_artifacts },
                              { label: 'Taps', value: campaignStats[cName].taps },
                              { label: 'Unique Visitors', value: campaignStats[cName].unique_visitors },
                              { label: 'Leads', value: campaignStats[cName].leads },
                              { label: 'Video Plays', value: campaignStats[cName].video_plays },
                              { label: 'PDF Downloads', value: campaignStats[cName].pdf_downloads },
                            ].map(stat => (
                              <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                                <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                                <p className="text-xs text-slate-500">{stat.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="divide-y divide-slate-100">
                        {campaignArtifacts.map(artifact => (
                          <GiveawayArtifactRow
                            key={artifact.artifact_id}
                            artifact={artifact}
                            profiles={profiles}
                            customers={customers}
                            onAssignProfile={handleAssignProfile}
                            onAssignCustomer={handleAssignCustomer}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty States */}
          {artifacts.length === 0 && activeTab !== 'cube' && (
            <div className="bg-white rounded-xl border border-slate-200 px-6 py-12 text-center">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No artifacts assigned to you yet</p>
              <p className="text-sm text-slate-400">Your admin will create and assign artifacts to your account</p>
            </div>
          )}
          {activeTab === 'personal' && personalArtifacts.length === 0 && artifacts.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 px-6 py-12 text-center">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No personal artifacts</p>
            </div>
          )}
          {activeTab === 'giveaway' && giveawayArtifacts.length === 0 && artifacts.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 px-6 py-12 text-center">
              <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No campaigns yet</p>
              <p className="text-sm text-slate-400">Select artifacts and assign them to a campaign</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ============== SUB-COMPONENTS ==============

const ArtifactRow = ({ artifact, profiles, onAssignProfile, selected, onToggleSelect }) => {
  const typeInfo = ARTIFACT_TYPES.find(t => t.value === artifact.artifact_type);
  return (
    <div className="px-6 py-4 hover:bg-slate-50" data-testid={`artifact-row-${artifact.artifact_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => onToggleSelect(artifact.artifact_id)} className="text-slate-400 hover:text-indigo-600" data-testid={`select-${artifact.artifact_id}`}>
            {selected ? <CheckSquare className="w-5 h-5 text-indigo-600" /> : <Square className="w-5 h-5" />}
          </button>
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
            {typeInfo?.icon}
          </div>
          <div>
            <p className="font-medium text-slate-900">{artifact.label || typeInfo?.label}</p>
            <p className="text-xs text-slate-400">{artifact.artifact_type} &middot; {artifact.artifact_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={artifact.assigned_profile_id || 'none'}
            onValueChange={(value) => onAssignProfile(artifact.artifact_id, value === 'none' ? null : value)}
          >
            <SelectTrigger className="w-40" data-testid={`profile-select-${artifact.artifact_id}`}><SelectValue placeholder="Assign profile" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not assigned</SelectItem>
              {profiles.map((profile) => <SelectItem key={profile.profile_id} value={profile.profile_id}>{profile.profile_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

const GiveawayArtifactRow = ({ artifact, profiles, customers, onAssignProfile, onAssignCustomer }) => {
  const typeInfo = ARTIFACT_TYPES.find(t => t.value === artifact.artifact_type);
  const assignedCustomer = customers.find(c => c.customer_id === artifact.customer_id);

  return (
    <div className="px-6 py-4 hover:bg-slate-50" data-testid={`artifact-row-${artifact.artifact_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
            {typeInfo?.icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-900">{artifact.label || typeInfo?.label}</p>
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Campaign</span>
            </div>
            <p className="text-xs text-slate-400">{artifact.artifact_type} &middot; {artifact.artifact_id}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 ml-16">
        <Select
          value={artifact.assigned_profile_id || 'none'}
          onValueChange={(value) => onAssignProfile(artifact.artifact_id, value === 'none' ? null : value)}
        >
          <SelectTrigger className="w-40" data-testid={`profile-select-${artifact.artifact_id}`}><SelectValue placeholder="Profile" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No profile</SelectItem>
            {profiles.map((p) => <SelectItem key={p.profile_id} value={p.profile_id}>{p.profile_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={artifact.customer_id || 'none'}
          onValueChange={(value) => onAssignCustomer(artifact.artifact_id, value === 'none' ? null : value)}
        >
          <SelectTrigger className="w-44" data-testid={`customer-select-${artifact.artifact_id}`}><SelectValue placeholder="Customer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No customer</SelectItem>
            {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {assignedCustomer && (
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Link2 className="w-3 h-3" />{assignedCustomer.name}{assignedCustomer.company ? ` (${assignedCustomer.company})` : ''}
          </span>
        )}
      </div>
    </div>
  );
};

const CubeConfigSection = ({ cubeConfig, profiles, schedules, activeProfile, onUpdateCube, userId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>QR Cube Configuration</h3>
      <div data-testid="schedule-indicator">
        {schedules.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium"><Clock className="w-3.5 h-3.5" /> Schedule ON</span>
            {activeProfile?.scheduled && activeProfile.profile && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium"><CheckCircle className="w-3.5 h-3.5" /> Active: {activeProfile.profile.profile_name}</span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-sm font-medium"><Clock className="w-3.5 h-3.5" /> Schedule OFF</span>
        )}
      </div>
    </div>
    <p className="text-sm text-slate-500 mb-4">Map each cube face (color) to a profile. When someone scans that face, they'll see the assigned profile.</p>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {Object.entries(PROFILE_COLORS).map(([color, styles]) => {
        const assignedProfileId = cubeConfig?.[`${color}_profile_id`];
        return (
          <div key={color} className="text-center" data-testid={`cube-face-${color}`}>
            <div className={`w-16 h-16 ${styles.bg} rounded-xl mx-auto mb-3 flex items-center justify-center`}><span className="text-white text-xs font-bold uppercase">{color}</span></div>
            <Select value={assignedProfileId || 'none'} onValueChange={(value) => onUpdateCube(color, value === 'none' ? null : value)}>
              <SelectTrigger className="w-full text-xs"><SelectValue placeholder="Assign profile" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not assigned</SelectItem>
                {profiles.map((profile) => <SelectItem key={profile.profile_id} value={profile.profile_id}>{profile.profile_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
    <div className="border-t border-slate-200 pt-6">
      <h4 className="text-md font-semibold text-slate-900 mb-4 flex items-center gap-2"><QrCode className="w-5 h-5 text-indigo-600" /> Download QR Codes</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        {Object.entries(PROFILE_COLORS).map(([color, styles]) => {
          const assignedProfileId = cubeConfig?.[`${color}_profile_id`];
          return (
            <div key={`qr-${color}`} className="text-center">
              <div className={`w-12 h-12 ${styles.bg} rounded-lg mx-auto mb-2 flex items-center justify-center`}><QrCode className="w-6 h-6 text-white" /></div>
              <p className="text-xs font-medium text-slate-700 mb-2">{color.toUpperCase()}</p>
              {assignedProfileId ? (
                <div className="flex flex-col gap-1">
                  <a href={`${API_BASE}/api/qr/profile/${assignedProfileId}?format=png`} download={`qr_${color}.png`} className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center justify-center gap-1" data-testid={`download-qr-${color}-png`}><FileImage className="w-3 h-3" /> PNG</a>
                  <a href={`${API_BASE}/api/qr/profile/${assignedProfileId}?format=svg`} download={`qr_${color}.svg`} className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center justify-center gap-1" data-testid={`download-qr-${color}-svg`}><FileText className="w-3 h-3" /> SVG</a>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Not assigned</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-center">
        <a href={`${API_BASE}/api/qr/cube/${userId}/print`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" data-testid="download-print-sheet"><Printer className="w-4 h-4" /> Download Print Sheet (PDF)</a>
      </div>
    </div>
  </div>
);

export default ArtifactsPage;
